import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { canModifyOwned, canOrg, canPlatform, type OrgAction, type OrgRole, type PlatformAction } from '@1qa/shared';
import { api, tokenStore } from './api';
import type { Me, MembershipSummary, User } from './types';

const ORG_KEY = '1qa.currentOrg';

interface AuthContextValue {
  user: User | null;
  memberships: MembershipSummary[];
  loading: boolean;
  /** Currently selected organization (null when the user belongs to none). */
  currentOrgId: string | null;
  currentOrgRole: OrgRole | null;
  setCurrentOrgId: (orgId: string) => void;
  signIn: (token: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<unknown>;
  can: (action: OrgAction, orgId?: string | null) => boolean;
  /** Change/delete something someone created (a test, test data): its creator or a Project Admin. */
  canModify: (action: OrgAction, ownerId: string | null | undefined, orgId?: string | null) => boolean;
  canPlatform: (action: PlatformAction) => boolean;
  roleIn: (orgId: string | null | undefined) => OrgRole | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [hasToken, setHasToken] = useState(() => !!tokenStore.get());
  const [selectedOrg, setSelectedOrg] = useState<string | null>(() => localStorage.getItem(ORG_KEY));

  const meQuery = useQuery<Me>({
    queryKey: ['me'],
    queryFn: api.me,
    enabled: hasToken,
    staleTime: 60_000,
    retry: false,
  });

  const user = hasToken ? (meQuery.data?.user ?? null) : null;
  const memberships = useMemo(() => (hasToken ? (meQuery.data?.memberships ?? []) : []), [hasToken, meQuery.data]);

  // Keep the selected org valid; fall back to the first membership.
  const currentOrgId = useMemo(() => {
    if (selectedOrg && memberships.some((m) => m.orgId === selectedOrg)) return selectedOrg;
    return memberships[0]?.orgId ?? null;
  }, [selectedOrg, memberships]);

  useEffect(() => {
    if (currentOrgId) localStorage.setItem(ORG_KEY, currentOrgId);
  }, [currentOrgId]);

  const roleIn = useCallback(
    (orgId: string | null | undefined) => memberships.find((m) => m.orgId === orgId)?.role ?? null,
    [memberships],
  );

  const signIn = useCallback(
    async (token: string) => {
      tokenStore.set(token);
      setHasToken(true);
      await qc.invalidateQueries();
      await qc.fetchQuery({ queryKey: ['me'], queryFn: api.me });
    },
    [qc],
  );

  const signOut = useCallback(() => {
    tokenStore.clear();
    setHasToken(false);
    qc.clear();
  }, [qc]);

  const value = useMemo<AuthContextValue>(() => {
    const platformRole = user?.platformRole ?? 'USER';
    return {
      user,
      memberships,
      loading: hasToken && meQuery.isPending,
      currentOrgId,
      currentOrgRole: roleIn(currentOrgId),
      setCurrentOrgId: (orgId) => setSelectedOrg(orgId),
      signIn,
      signOut,
      refresh: () => meQuery.refetch(),
      can: (action, orgId = currentOrgId) => !!user && canOrg(platformRole, roleIn(orgId), action),
      canModify: (action, ownerId, orgId = currentOrgId) =>
        !!user && !!ownerId && canModifyOwned(platformRole, roleIn(orgId), action, user.id, ownerId),
      canPlatform: (action) => !!user && canPlatform(platformRole, action),
      roleIn,
    };
  }, [user, memberships, hasToken, meQuery, currentOrgId, roleIn, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
